import React, { useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { TrendingUp, TrendingDown, CheckCircle2 } from 'lucide-react';

export interface VitalSignDataPoint {
  date: string;
  rawDate?: string;
  value: number;
  value2?: number; // For dual-line charts like blood pressure
}

export interface VitalSignTrendChartProps {
  title: string;
  latestValue: string;
  trendDirection: 'UP' | 'DOWN' | null;
  data: VitalSignDataPoint[];
  isDualLine?: boolean;
  yAxisLabel?: string;
  line1Label?: string;
  line2Label?: string;
  inCarePlan?: boolean;
}

interface ChartDataPoint {
  date: string;
  rawDate?: string;
  prevValue?: number;
  recentValue?: number;
  prevValue2?: number;
  recentValue2?: number;
}

const CustomVitalTooltip: React.FC<{
  active?: boolean;
  payload?: Array<{ payload: ChartDataPoint }>;
  label?: string;
  isDualLine: boolean;
}> = ({ active, payload, label, isDualLine }) => {
  if (!active || !payload || payload.length === 0) return null;

  const dataPoint = payload[0]?.payload;
  if (!dataPoint) return null;

  const primaryValue = dataPoint.recentValue ?? dataPoint.prevValue;
  const primaryColor = dataPoint.recentValue !== undefined ? '#228189' : '#9ca3af';

  const secondaryValue = isDualLine
    ? (dataPoint.recentValue2 ?? dataPoint.prevValue2)
    : undefined;
  const secondaryColor = isDualLine
    ? (dataPoint.recentValue2 !== undefined ? '#228189' : '#9ca3af')
    : undefined;

  const displayDate = (() => {
    if (!dataPoint.rawDate) return label;
    // For datetime strings, parse with Date() so we can render the time portion.
    // For date-only strings (YYYY-MM-DD), do NOT use new Date(): JS parses them as
    // UTC midnight, and getDate() returns local-time day — which shifts the date
    // back by one in any negative-UTC-offset timezone. Use the pre-formatted
    // dataPoint.date label instead, which was built via string manipulation.
    const hasTime = dataPoint.rawDate.includes('T') && dataPoint.rawDate.length > 10;
    if (!hasTime) return dataPoint.date ?? label;
    const d = new Date(dataPoint.rawDate);
    if (isNaN(d.getTime())) return dataPoint.date ?? label;
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${month}/${day} ${hours}:${minutes}`;
  })();

  return (
    <div
      style={{
        backgroundColor: '#ffffff',
        border: '1px solid #e5e7eb',
        borderRadius: '4px',
        padding: '8px 12px',
        fontSize: '12px',
      }}
    >
      <p style={{ fontWeight: 600, marginBottom: 4 }}>{displayDate}</p>
      {primaryValue !== undefined && (
        <p style={{ color: primaryColor, margin: 0 }}>
          {isDualLine ? 'Systolic: ' : ''}{primaryValue}
        </p>
      )}
      {secondaryValue !== undefined && (
        <p style={{ color: secondaryColor, margin: 0 }}>
          Diastolic: {secondaryValue}
        </p>
      )}
    </div>
  );
};

export const VitalSignTrendChart: React.FC<VitalSignTrendChartProps> = ({
  title,
  latestValue,
  trendDirection,
  data,
  isDualLine = false,
  yAxisLabel = '',
  line1Label = 'Previous 7 Days',
  line2Label = 'Last 3 Days',
  inCarePlan,
}) => {
  // Compute dynamic Y-axis domain based on actual data values
  const yDomain = useMemo<[number, number]>(() => {
    if (data.length === 0) return [0, 100];
    const allValues: number[] = [];
    for (const point of data) {
      allValues.push(point.value);
      if (point.value2 !== undefined) allValues.push(point.value2);
    }
    if (allValues.length === 0) return [0, 100];
    const min = Math.min(...allValues);
    const max = Math.max(...allValues);
    const range = max - min;
    const padding = range > 0 ? range * 0.15 : 5;
    const niceMin = Math.floor((min - padding) / 5) * 5;
    const niceMax = Math.ceil((max + padding) / 5) * 5;
    return [Math.max(0, niceMin), niceMax];
  }, [data]);

  const chartData = useMemo<ChartDataPoint[]>(() => {
    if (data.length === 0) return [];

    // "Last 3 days" = 3 calendar days back from today
    const now = new Date();
    const cutoff = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 3);
    const cutoffStr = cutoff.toISOString().split('T')[0]; // YYYY-MM-DD

    // Find the first index whose date falls within the last 3 days
    let splitIndex = data.length; // default: nothing is recent
    for (let i = 0; i < data.length; i++) {
      const dayStr = data[i].rawDate?.split('T')[0];
      if (dayStr && dayStr >= cutoffStr) {
        splitIndex = i;
        break;
      }
    }

    return data.map((point, index) => {
      const entry: ChartDataPoint = {
        date: point.date,
        rawDate: point.rawDate,
      };

      // Previous segment: up to and including overlap point
      if (index <= splitIndex) {
        entry.prevValue = point.value;
        if (isDualLine && point.value2 !== undefined) {
          entry.prevValue2 = point.value2;
        }
      }

      // Recent segment: from overlap point onward
      if (index >= splitIndex) {
        entry.recentValue = point.value;
        if (isDualLine && point.value2 !== undefined) {
          entry.recentValue2 = point.value2;
        }
      }

      return entry;
    });
  }, [data, isDualLine]);

  const TrendIcon = trendDirection === 'UP' ? TrendingUp : TrendingDown;
  const trendColor = trendDirection === 'UP' ? '#228189' : '#B02828';
  const hasTrend = trendDirection !== null;

  return (
    <div className="border border-gray-200 rounded-lg p-4 h-full flex flex-col bg-white">
      {/* Header with title and trend badge */}
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-base font-semibold">{title}</h3>
        {hasTrend && (
          <div className="flex items-center gap-1">
            <TrendIcon size={16} style={{ color: trendColor }} />
            <span className="text-xs font-semibold" style={{ color: trendColor }}>
              {trendDirection}
            </span>
          </div>
        )}
      </div>

      {/* Chart */}
      <div className="flex-1 min-h-[200px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 11 }}
              tickLine={false}
              axisLine={{ stroke: '#e5e7eb' }}
              angle={-35}
              textAnchor="end"
              height={50}
            />
            <YAxis
              domain={yDomain}
              tick={{ fontSize: 12 }}
              tickLine={false}
              axisLine={{ stroke: '#e5e7eb' }}
              label={yAxisLabel ? { value: yAxisLabel, angle: -90, position: 'insideLeft' } : undefined}
            />
            <Tooltip content={<CustomVitalTooltip isDualLine={isDualLine} />} />

            {/* Previous days - gray solid line */}
            <Line
              type="monotone"
              dataKey="prevValue"
              stroke="#9ca3af"
              strokeWidth={2}
              dot={{ fill: '#9ca3af', r: 4 }}
              activeDot={{ r: 6 }}
              name={line1Label}
              connectNulls={false}
            />

            {/* Last 3 days - teal solid line */}
            <Line
              type="monotone"
              dataKey="recentValue"
              stroke="#228189"
              strokeWidth={2}
              dot={{ fill: '#228189', r: 4 }}
              activeDot={{ r: 6 }}
              name={line2Label}
              connectNulls={false}
            />

            {isDualLine && (
              <>
                {/* Previous days - gray dashed line (diastolic) */}
                <Line
                  type="monotone"
                  dataKey="prevValue2"
                  stroke="#9ca3af"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={{ fill: '#9ca3af', r: 4 }}
                  activeDot={{ r: 6 }}
                  name="Diastolic (Prev)"
                  connectNulls={false}
                />

                {/* Last 3 days - teal dashed line (diastolic) */}
                <Line
                  type="monotone"
                  dataKey="recentValue2"
                  stroke="#228189"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={{ fill: '#228189', r: 4 }}
                  activeDot={{ r: 6 }}
                  name="Diastolic (Recent)"
                  connectNulls={false}
                />
              </>
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Custom Legend Footer */}
      <div className="flex items-center justify-between pt-2 mt-2">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-gray-400 inline-block" />
            <span className="text-xs text-gray-600">{line1Label}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-[#228189] inline-block" />
            <span className="text-xs text-gray-600">{line2Label}</span>
          </div>
        </div>
        <div className="text-xs text-gray-700">
          Latest: <span className="font-bold">{latestValue}</span>
        </div>
      </div>

      {/* Care Plan Status (Hidden until feature implemented) */}
      {false && inCarePlan !== undefined && (
        <div className="flex items-center gap-1.5 pt-2 text-[#23833F]">
          <CheckCircle2 className="w-4 h-4" />
          <span className="text-xs font-medium">In Care Plan</span>
        </div>
      )}
    </div>
  );
};
